import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, FlaskConical, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/cn';
import { useTriggerAutoRebook } from '@/lib/disruptions/use-disruptions';
import { BulkInProgressError } from '@/lib/disruptions/types';
import type { BulkAcceptPreview } from '@/lib/disruptions/api';
import { formatPriceMinor, operatorLabel } from './labels';

// disruption-roster-mvp-parity (epic NEX-369 / U3, ticket NEX-375 — the FINAL
// slice). The AUTOMATIC ACCEPTANCE panel: the MVP BulkActionBar automatic-
// acceptance section (QUALITY rank · USD CAP · DRY-RUN · SIMULATE/ACCEPT),
// mounted into U2's `automatic-acceptance-slot` in the roster action sidebar.
//
// It drives the FROZEN operator-bff seam `auto-rebook@v2` (B2) via the existing
// `useTriggerAutoRebook` mutation, now carrying the v2 modifiers
// `{ acceptTopRank, qualityRank, priceCapUsd, dryRun }` — the cap is the PRICE the
// airline accepts per PNR (commercial-contract P7, `caps:price-basis@v1`):
//   • DRY-RUN (default ON) → SIMULATE: a ONE-SHOT mutation that books NOTHING and
//     returns an accept `preview` (wouldAccept / skippedByCap / wouldPriceMinor) so
//     the operator simulates the rule before committing. This is NOT a live run —
//     no polling, no interval, no WS run-state; the preview is the mutation result.
//   • DRY-RUN OFF → ACCEPT AND BOOK: kicks off the real bulk run. The BFF records
//     the run + pushes run-state over the disruption WS room; the sibling
//     SidebarProgressPanel (already mounted above this in the sidebar) reflects the
//     live progress over that WS push — this panel does NO polling of its own.

interface AutomaticAcceptancePanelProps {
  disruptionUrn: string;
}

type Quality = '1' | '2' | '3';

/** Short label KEY per rank for the live preview line — this map holds keys,
 *  never language; the panel resolves them with `t`. */
const QUALITY_SHORT: Record<Quality, string> = {
  '1': 'autoAccept.quality.short1',
  '2': 'autoAccept.quality.short2',
  '3': 'autoAccept.quality.short3',
};

/** Minor units (USD cents) → a `$1,234` string. */
export function AutomaticAcceptancePanel({ disruptionUrn }: AutomaticAcceptancePanelProps) {
  const { t } = useTranslation('disruptions');
  const [quality, setQuality] = useState<Quality>('1');
  const [priceCap, setPriceCap] = useState<string>('800');
  const [dryRun, setDryRun] = useState(true);

  // Last dry-run preview (SIMULATE) — cleared when a live run starts or on a fresh
  // simulate. A live run has no preview; it surfaces `startedRunId` instead.
  const [preview, setPreview] = useState<BulkAcceptPreview | null>(null);
  const [startedRunId, setStartedRunId] = useState<string | null>(null);
  // A 409 (another operator's run holds the disruption lock) names the holder.
  const [conflict, setConflict] = useState<{ operatorUrn: string } | null>(null);

  const autoRebook = useTriggerAutoRebook(disruptionUrn);

  const capUsd = priceCap.trim() === '' ? undefined : Number(priceCap);
  const capValid = capUsd === undefined || (Number.isFinite(capUsd) && capUsd >= 0);
  const qualityRank = Number(quality) as 1 | 2 | 3;

  const onSubmit = (): void => {
    setConflict(null);
    autoRebook.mutate(
      {
        acceptTopRank: true,
        qualityRank,
        dryRun,
        ...(capUsd !== undefined && Number.isFinite(capUsd) ? { priceCapUsd: capUsd } : {}),
      },
      {
        onSuccess: (res) => {
          if (res.preview) {
            // SIMULATE — a dry-run returns the preview; nothing was booked.
            setPreview(res.preview);
            setStartedRunId(null);
          } else {
            // ACCEPT AND BOOK — the live run started; the sidebar progress panel
            // now shows it live over the WS. Clear any stale simulation.
            setStartedRunId(res.runId);
            setPreview(null);
          }
        },
        onError: (err) => {
          if (err instanceof BulkInProgressError) {
            setConflict({ operatorUrn: err.inProgressBy });
          }
        },
      },
    );
  };

  const busy = autoRebook.isPending;
  // A non-409 failure (503 etc.) is the retryable "couldn't run" hint. A 409 is
  // surfaced separately (the lock banner) — not an error from the operator's POV.
  const hardError = !!autoRebook.error && !(autoRebook.error instanceof BulkInProgressError);
  const disabled = busy || !capValid;

  // Live preview line — describes exactly what the click will do (mirrors MVP).
  const capText =
    capUsd !== undefined
      ? t('autoAccept.preview.withCap', { amount: capUsd.toLocaleString('en-US') })
      : t('autoAccept.preview.noCap');
  const previewLine = t(
    dryRun ? 'autoAccept.preview.simulate' : 'autoAccept.preview.live',
    { quality: t(QUALITY_SHORT[quality]), cap: capText },
  );

  return (
    <div className="flex flex-col gap-2.5" data-testid="automatic-acceptance-panel">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {t('autoAccept.title')}
      </h3>

      {/* QUALITY + USD CAP */}
      <div className="grid grid-cols-2 gap-2">
        <label className="flex flex-col gap-1">
          <span className="text-[11px] font-medium text-muted-foreground">
            {t('autoAccept.quality.label')}
          </span>
          <select
            value={quality}
            onChange={(e) => setQuality(e.target.value as Quality)}
            disabled={busy}
            className="h-8 rounded-md border border-input bg-background px-2 text-xs text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
            data-testid="aa-quality"
            aria-label={t('autoAccept.quality.aria')}
          >
            <option value="1">{t('autoAccept.quality.opt1')}</option>
            <option value="2">{t('autoAccept.quality.opt2')}</option>
            <option value="3">{t('autoAccept.quality.opt3')}</option>
          </select>
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-[11px] font-medium text-muted-foreground">
            {t('autoAccept.cap.label')}
          </span>
          <input
            type="number"
            min={0}
            step={50}
            inputMode="numeric"
            placeholder={t('autoAccept.cap.placeholder')}
            value={priceCap}
            onChange={(e) => setPriceCap(e.target.value)}
            disabled={busy}
            className={cn(
              'h-8 rounded-md border bg-background px-2 text-xs text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50',
              capValid ? 'border-input' : 'border-destructive',
            )}
            data-testid="aa-cost-cap"
            aria-label={t('autoAccept.cap.aria')}
            aria-invalid={!capValid}
          />
        </label>
      </div>

      {/* DRY-RUN toggle — default ON (blue cue) / OFF (amber cue). */}
      <label
        className={cn(
          'flex cursor-pointer items-center gap-2 rounded-md border px-2 py-1.5 text-[11px] transition-colors',
          dryRun
            ? 'border-sky-300 bg-sky-50 text-sky-900 dark:border-sky-900/40 dark:bg-sky-950/40 dark:text-sky-200'
            : 'border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/40 dark:text-amber-200',
        )}
      >
        <input
          type="checkbox"
          checked={dryRun}
          onChange={(e) => setDryRun(e.target.checked)}
          disabled={busy}
          className="h-3.5 w-3.5 accent-sky-600"
          data-testid="aa-dry-run"
        />
        <span className="inline-flex items-center gap-1 font-mono font-semibold uppercase tracking-wide">
          <FlaskConical className="h-3.5 w-3.5" aria-hidden />
          {t('autoAccept.dryRun.label')}
        </span>
        <span className="ml-auto text-[10px] uppercase tracking-wide opacity-70">
          {dryRun ? t('autoAccept.dryRun.recommended') : t('autoAccept.dryRun.live')}
        </span>
      </label>

      {/* Live preview line — token-themed dry-run (sky) vs live (amber) cue. */}
      <p
        className={cn(
          'rounded-md border px-2 py-1.5 text-[11px] leading-snug',
          dryRun
            ? 'border-sky-200 bg-sky-50 text-sky-900 dark:border-sky-900/40 dark:bg-sky-950/40 dark:text-sky-200'
            : 'border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/40 dark:text-amber-200',
        )}
        data-testid="aa-preview-line"
      >
        {previewLine}
      </p>

      {/* SIMULATE (dry-run) / ACCEPT AND BOOK (live). */}
      <Button
        type="button"
        size="sm"
        variant={dryRun ? 'outline' : 'default'}
        disabled={disabled}
        onClick={onSubmit}
        className="w-full justify-center gap-1.5"
        data-testid="aa-submit"
      >
        {busy ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
        ) : (
          <CheckCircle2 className="h-3.5 w-3.5" aria-hidden />
        )}
        {dryRun ? t('autoAccept.actions.simulate') : t('autoAccept.actions.accept')}
      </Button>

      {/* ── Result / status feed ─────────────────────────────────────────────── */}

      {/* 409 — another operator's run holds the disruption lock. */}
      {conflict ? (
        <p
          className="text-[11px] text-amber-600 dark:text-amber-400"
          role="status"
          data-testid="aa-locked"
        >
          {t('autoAccept.status.locked', { operator: operatorLabel(conflict.operatorUrn) })}
        </p>
      ) : hardError ? (
        <p className="text-[11px] text-destructive" role="alert" data-testid="aa-error">
          {t('autoAccept.status.error')}
        </p>
      ) : preview ? (
        <PreviewResult preview={preview} />
      ) : startedRunId ? (
        <p className="text-[11px] text-muted-foreground" role="status" data-testid="aa-run-started">
          {t('autoAccept.status.started')}
        </p>
      ) : (
        <p className="text-[11px] text-muted-foreground" data-testid="aa-empty">
          {t('autoAccept.status.empty')}
        </p>
      )}
    </div>
  );
}

/** The dry-run simulation outcome — a one-shot mutation result (NOT a live feed). */
function PreviewResult({ preview }: { preview: BulkAcceptPreview }) {
  const { t } = useTranslation('disruptions');
  return (
    <div
      className="flex flex-col gap-1 rounded-md border border-border bg-card p-2 text-[11px]"
      data-testid="aa-preview-result"
    >
      <span className="font-semibold uppercase tracking-wide text-muted-foreground">
        {t('autoAccept.result.title')}
      </span>
      <div className="flex items-center justify-between">
        <span className="text-emerald-600 dark:text-emerald-400" data-testid="aa-would-accept">
          {t('autoAccept.result.wouldAccept', {
            n: preview.wouldAccept.toLocaleString('en-US'),
          })}
        </span>
        {/* airline-money@v2 — the airline's PRICE; null (not priced) → «No disponible». */}
        <span
          className={
            preview.wouldPriceMinor === null
              ? 'text-muted-foreground'
              : 'tabular-nums font-medium text-foreground'
          }
          data-testid="aa-would-price"
        >
          {formatPriceMinor(preview.wouldPriceMinor, 'USD', t)}
        </span>
      </div>
      {preview.skippedByCap > 0 ? (
        <span className="text-amber-600 dark:text-amber-400" data-testid="aa-skipped-cap">
          {t('autoAccept.result.skippedByCap', {
            n: preview.skippedByCap.toLocaleString('en-US'),
          })}
        </span>
      ) : null}
    </div>
  );
}
